import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Radio,
  Button,
  Chip,
  Stack,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";
import { Organization } from "@/contexts/DealContext";
import { organizationControllerCreateRequest } from "@/altgency_api/sdk.gen";

interface BankFromAI {
  name: string;
  commitment?: number;
  registration_number?: string | null;
  matched_organisations: Organization[];
}

interface Props {
  bank: BankFromAI;
  allOrganizations: Organization[];
  onValidate: (org: {
    organization?: Organization | null;
    commitment?: number;
    newOrgData?: { name: string; registration_number?: string | null };
  }) => void;
}

const OrganizationMatcher: React.FC<Props> = ({ bank, allOrganizations, onValidate }) => {
  const [localMatchedOrgs, setLocalMatchedOrgs] = useState<Organization[]>(
    bank?.matched_organisations || []
  );
  const [selected, setSelected] = useState<Organization | null>(null);
  const [commitment, setCommitment] = useState<number>(bank?.commitment || 0);
  const [openNewModal, setOpenNewModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newOrgName, setNewOrgName] = useState(bank?.name);
  const [newOrgRegistration, setNewOrgRegistration] = useState(bank?.registration_number || "");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    setLocalMatchedOrgs(bank?.matched_organisations || []);
  }, [bank]);

  const newOrganizationRequest = (name: string, registration: string) => {
    setLoading(true);
    organizationControllerCreateRequest({ body: { name, registration } })
      .then((response) => {
        if (response.error) {
          setToastMessage((response.error as any)?.message || "An error occurred.");
        } else {
          setToastMessage("The organization creation request has been successfully sent!");
        }
        setOpenNewModal(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error creating organization:", error);
        setToastMessage("An error occurred while sending the organization creation request.");
        setLoading(false);
      });
  };

  const hasExactMatch =
    bank?.registration_number &&
    localMatchedOrgs.some(
      (org) => org.registration?.replace(/\s/g, "") === bank.registration_number?.replace(/\s/g, "")
    );

  // ✅ Pré-sélectionner automatiquement si match parfait
  useEffect(() => {
    if (hasExactMatch) {
      const org = localMatchedOrgs.find(
        (org) =>
          org.registration?.replace(/\s/g, "") === bank?.registration_number?.replace(/\s/g, "")
      );
      if (org) setSelected(org);
    }
  }, [hasExactMatch, localMatchedOrgs, bank?.registration_number]);

  useEffect(() => {
    onValidate({ organization: selected, commitment });
  }, [selected, commitment]);

  // 📝 Explications contextuelles
  let explanation = "";
  if (hasExactMatch) {
    explanation = "We have found an exact match in our database, it is pre-selected below.";
  } else if (localMatchedOrgs.length > 1) {
    explanation =
      "Several organizations seem to match, please select the correct one from the list below.";
  } else if (localMatchedOrgs.length === 1) {
    explanation =
      "We have found an organization that might match, please confirm if it is the correct one.";
  } else {
    explanation =
      "We could not find any matching organization. You can search manually in our database or create a new organization.";
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6">{bank?.name}</Typography>

        <Stack spacing={1} sx={{ mt: 1 }}>
          {/* Commitment éditable */}
          {bank?.commitment !== undefined && (
            <TextField
              label="Commitment"
              // type="number"
              value={
                commitment
                  ? Number(commitment.toString().replaceAll(",", "")).toLocaleString("fi-FI", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  : ""
              }
              onChange={(e) => {
                const rawValue = e.target.value.replace(/\s/g, "").replaceAll(",", ".");
                if (!isNaN(Number(rawValue))) {
                  setCommitment(Number(rawValue));
                }
              }}
              size="small"
            />
          )}

          {bank?.registration_number ? (
            <Typography variant="body2" color="text.secondary">
              Registration found: {bank.registration_number}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No registration number detected for this bank
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ my: 1 }}>
          {hasExactMatch ? (
            <Chip label="Perfect match" color="success" />
          ) : localMatchedOrgs.length > 0 ? (
            <Chip label="Partial match" color="warning" />
          ) : (
            <Chip label="No match found" color="error" />
          )}
        </Stack>

        {/* Explication contextuelle */}
        <Typography variant="body2" sx={{ mb: 2 }}>
          {explanation}
        </Typography>

        {/* Liste des organisations candidates */}
        {localMatchedOrgs.map((org) => (
          <Card
            key={org.id}
            variant="outlined"
            sx={{
              my: 1,
              p: 1,
              border: selected?.id === org.id ? "2px solid #1976d2" : "1px solid #ccc",
              cursor: "pointer",
            }}
            onClick={() => setSelected(org)}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Radio checked={selected?.id === org.id} />
              <div>
                <Typography variant="subtitle1">{org.name}</Typography>
                {org.shortName && (
                  <Typography variant="body2" color="text.secondary">
                    {org.shortName}
                  </Typography>
                )}
                {org.registration && (
                  <Typography variant="body2">Registration: {org.registration}</Typography>
                )}
                {org.address && (
                  <Typography variant="body2" color="text.secondary">
                    {org.address.line1}, {org.address.city.postalCode} {org.address.city.name},{" "}
                    {org.address.city.country.name}
                  </Typography>
                )}
              </div>
            </Stack>
          </Card>
        ))}
        {localMatchedOrgs.length > 0 && (
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            If none of the organizations above match, you can search our database or ask an
            administrator to create a new organization.
          </Typography>
        )}
        {/* Recherche manuelle */}
        <Autocomplete
          options={allOrganizations}
          getOptionLabel={(option) => `${option.name} (${option.registration || "no reg."})`}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search all organizations"
              variant="outlined"
              fullWidth
              placeholder="Search for an existing bank by name or number…"
              sx={{ mt: 2 }}
            />
          )}
          onChange={(_, value) => {
            if (value) {
              // éviter doublons
              const alreadyInList = localMatchedOrgs.some((org) => org.id === value.id);
              if (!alreadyInList) {
                setLocalMatchedOrgs((prev) => [...prev, value]);
              }
              setSelected(value);
            }
          }}
        />

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          {/* <Button
            variant="contained"
            disabled={!selected}
            onClick={() =>
              selected &&
              onValidate({ organization: selected, commitment })
            }
          >
            Valider
          </Button> */}
          <Button variant="outlined" onClick={() => setOpenNewModal(true)}>
            Report a missing organization
          </Button>
        </Stack>
      </CardContent>

      {/* Modale de création */}
      <Dialog open={openNewModal} onClose={() => setOpenNewModal(false)}>
        <DialogTitle>
          Report a missing organization, so we can include it in our directory
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Organization Name"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              fullWidth
            />
            <TextField
              label="SIRET / Registration"
              value={newOrgRegistration}
              onChange={(e) => setNewOrgRegistration(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={loading}
            loading={loading}
            onClick={() => {
              setLoading(true);
              newOrganizationRequest(newOrgName, newOrgRegistration);
            }}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!toastMessage}
        autoHideDuration={6000}
        onClose={() => setToastMessage(null)}
        message={toastMessage}
      />
    </Card>
  );
};

export default OrganizationMatcher;
